import { useCallback, useEffect } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import { z } from 'zod'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { FormField } from '@/components/ui/form/form-field.tsx'
import { useRequestReferral } from '@/modules/referral/referral-request.ts'

export interface ReferralDialogProps {
  isOpen: boolean
  onClose: () => void
  onSubmitted?: (values: Schema) => void
}

const defaultValues: Schema = {
  url: '',
}

const Schema = z.object({
  url: z.string().min(1, 'The link is too short'),
})

type Schema = z.infer<typeof Schema>

const resolver = zodResolver(Schema)

const prefix = 'https://creatorhe.ro/'

export const ReferralDialog = (props: ReferralDialogProps) => {
  const { isOpen, onSubmitted, onClose } = props

  const form = useForm<Schema>({ defaultValues, resolver })

  useEffect(() => form.reset(), [form, isOpen])
  const createLink = useRequestReferral()

  const handleSubmit = useCallback(
    (state: Schema) => {
      createLink(prefix + state.url)
      onSubmitted?.(state)
      onClose()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createLink, onClose],
  )

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
    >
      <Dialog.Header>
        <Dialog.Title className="text-center">
          Request Referral Link
        </Dialog.Title>
        <Dialog.Description className="text-center">
          Invite your friends to CreatorHero
        </Dialog.Description>
      </Dialog.Header>
      <p className="mb-4 mt-4 block text-xs text-title">
        Due to high demand we’re releasing a{' '}
        <strong className="text-title underline">referral program</strong>.
        <br />
        Refer any friend or agency to CreatorHero and receive{' '}
        <strong className="text-title underline">
          40% commission for life
        </strong>
        .
        <br />
        <br />
        Enter your wished link name and press done to receive your personalized
        link within the next 5 minutes.
      </p>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <FormField
            id="url-name"
            name="url"
            label="Referral Link"
            placeholder="Name"
            className="col-span-12"
            prefix={
              <span className="rounded-md bg-secondary-50 px-2 py-1 dark:bg-secondary-500/60">
                {prefix}
              </span>
            }
          />
          <Dialog.Footer {...props}>
            <Button
              type="submit"
              className="w-full"
            >
              Done
            </Button>
          </Dialog.Footer>
        </form>
      </FormProvider>
    </Dialog>
  )
}
